import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import FilmStripLoader from '../components/FilmStripLoader';

const WatchMovie = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasActiveSubscription } = useAuth();
  const [movie, setMovie] = useState(null);
  const [videoToken, setVideoToken] = useState(null);
  const [showLoader, setShowLoader] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const iframeRef = useRef(null);
  const videoContainerRef = useRef(null);
  const hasTrackedView = useRef(false);

  useEffect(() => {
    if (!hasActiveSubscription) {
      navigate('/subscriptions');
      return;
    }
    setIsLoading(true);
    const loadData = async () => {
      await Promise.all([fetchMovie(), fetchVideoToken()]);
      setTimeout(() => {
        setIsLoading(false);
        setTimeout(() => {
          setShowLoader(false);
        }, 600);
      }, 500);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, hasActiveSubscription]);

  useEffect(() => {
    // Security: Disable common inspection methods
    const handleKeyDown = (e) => {
      if (e.key === 'F12' || 
          (e.ctrlKey && e.shiftKey && ['I', 'J', 'C'].includes(e.key)) ||
          (e.ctrlKey && ['U', 'S'].includes(e.key))) {
        e.preventDefault();
        return false;
      }
    };
    
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };
    
    const handleSelectStart = (e) => {
      e.preventDefault();
      return false;
    };
    
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('selectstart', handleSelectStart);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('selectstart', handleSelectStart);
    };
  }, []);

  // Request fullscreen on mobile when video loads
  useEffect(() => {
    if (!iframeRef.current || !videoContainerRef.current || !movie || !videoToken) return;

    // Detect mobile devices
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile && iframeRef.current.src) {
      const requestFullscreen = () => {
        const container = videoContainerRef.current;
        if (!container) return;

        try {
          if (container.requestFullscreen) {
            container.requestFullscreen().catch(err => {
              console.log('Fullscreen request failed (standard):', err);
            });
          } else if (container.webkitRequestFullscreen) {
            container.webkitRequestFullscreen().catch(err => {
              console.log('Fullscreen request failed (webkit):', err);
            });
          } else if (container.mozRequestFullScreen) {
            container.mozRequestFullScreen().catch(err => {
              console.log('Fullscreen request failed (moz):', err);
            });
          } else if (container.msRequestFullscreen) {
            container.msRequestFullscreen().catch(err => {
              console.log('Fullscreen request failed (ms):', err);
            });
          }
        } catch (err) {
          console.log('Fullscreen error:', err);
        }
      };

      // Wait for iframe to load then request fullscreen
      const checkIframe = () => {
        const iframe = iframeRef.current;
        if (iframe && iframe.src && iframe.src.length > 0) {
          // Small delay to ensure iframe is ready
          setTimeout(requestFullscreen, 1000);
        } else {
          setTimeout(checkIframe, 200);
        }
      };

      checkIframe();
    }
  }, [movie, videoToken]);

  const fetchMovie = async () => {
    try {
      const res = await axios.get(`/api/movies/${id}`);
      setMovie(res.data);
    } catch (error) {
      console.error('Error fetching movie:', error);
    }
  };

  const fetchVideoToken = async () => {
    try {
      const res = await axios.get(`/api/movies/${id}/watch`);
      setVideoToken(res.data.token);
      
      // Detect mobile devices
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (isMobile) {
        // On mobile, use the optimized embed route for better mobile support
        const embedUrl = `/api/stream/embed/${res.data.token}`;
        if (iframeRef.current) {
          iframeRef.current.src = embedUrl;
        }
      } else {
        // On desktop, get direct embed URL from backend
        const embedRes = await axios.get(`/api/stream/${res.data.token}`);
        if (embedRes.data.success && embedRes.data.embedUrl) {
          // Set iframe src directly to Bunny Stream embed URL (no wrapper iframe)
          if (iframeRef.current) {
            iframeRef.current.src = embedRes.data.embedUrl;
          }
        }
      }
      
    } catch (error) {
      console.error('Error fetching video token:', error);
      if (error.response?.status === 403) {
        navigate('/subscriptions');
      }
    }
  };

  const saveWatchHistory = async () => {
    try {
      // Note: We can't track actual video progress due to iframe cross-origin restrictions
      // So we just track that the user watched this movie
      await axios.post(`/api/movies/${id}/history`, { timestamp: 0 });
      console.log('Watch history saved for movie:', id);
    } catch (error) {
      console.error('Error saving history:', error);
    }
  };

  // Track initial view only once when video loads
  useEffect(() => {
    if (!movie || !videoToken || hasTrackedView.current) return;

    // Wait a few seconds to ensure they actually started watching
    const trackTimer = setTimeout(() => {
      saveWatchHistory();
      hasTrackedView.current = true;
    }, 5000); // Track after 5 seconds of watching

    return () => clearTimeout(trackTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, movie, videoToken]);

  // Get embed URL - set dynamically to hide from initial HTML
  // const getEmbedUrl = () => {
  //   // Return empty initially, will be set dynamically via ref
  //   return '';
  // };

  if (!hasActiveSubscription) {
    return null;
  }

  if (showLoader || !movie || !videoToken) {
    return (
      <div className={isLoading ? '' : 'fade-out-loader'}>
        <FilmStripLoader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      {/* Bunny Stream Video Player - Direct embed, no wrapper iframe */}
      <div 
        ref={videoContainerRef}
        style={{ position: 'relative', paddingTop: '56.25%', width: '100%', height: 0 }}
        onContextMenu={(e) => e.preventDefault()}
      >
        <iframe
          ref={iframeRef}
          src=""
          title="Video Player"
          loading="eager"
          style={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none',
            touchAction: 'none'
          }}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          playsInline
          webkit-playsinline="true"
          frameBorder="0"
          scrolling="no"
          onLoad={() => {
            // Video loaded successfully
            console.log('Video player loaded');
          }}
          onError={(e) => {
            console.error('Video player error:', e);
          }}
        />
      </div>

      {/* Video Info */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">{movie.title}</h1>
            <p className="text-gray-300">{movie.description}</p>
          </div>
          <Link
            to={`/movie/${id}`}
            className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition"
          >
            Retour
          </Link>
        </div>
      </div>

    </div>
  );
};

export default WatchMovie;
